import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';

export interface Reward {
  id: number;
  reward_name: string;
  amount: string;
  points: string;
  is_default?: number;
  created_at?: string;
  updated_at?: string;
}

export interface RewardFormData {
  reward_name: string;
  amount: string;
  points: string;
  is_default?: boolean;
}

export interface RewardResponse {
  error: boolean;
  message: string;
  data?: Reward | Reward[];
}

export interface RewardRequest {
  reward_name: string;
  amount: string;
  points: string;
  is_default?: boolean;
}

export interface SetDefaultRequest {
  id: number;
}

// Validation helpers
export const validateAmount = (value: string): boolean => {
  const num = parseFloat(value);
  return !isNaN(num) && num >= 0;
};

export const validatePoints = (value: string): boolean => {
  const num = parseFloat(value);
  return !isNaN(num) && num >= 0;
};

export const rewardApi = createApi({
  reducerPath: 'rewardApi',
  baseQuery: fetchBaseQuery({
    baseUrl: process.env.REACT_APP_SERVER_ENDPOINT,
    prepareHeaders: (headers) => {
      const token = localStorage.getItem('admin_token');
      if (token) {
        headers.set('Authorization', `Bearer ${token}`);
      }
      headers.set('authkey', '7b4e1fd03f57885c1ff900ad76ea3810bb7160c7157a4c31cf2de3');
      headers.set('Content-Type', 'application/x-www-form-urlencoded');
      return headers;
    },
  }),
  tagTypes: ['Reward'],
  endpoints: (build) => ({
    // List all rewards
    listRewards: build.query<RewardResponse, void>({
      query: () => ({
        url: 'user/rewardconversionlist',
        method: 'POST',
        body: new URLSearchParams(),
      }),
      providesTags: ['Reward'],
      transformResponse: (response: any) => ({
        error: response.error || false,
        message: response.message || '',
        data: response.data || [],
      }),
    }),

    // Add new reward (CREATE/UPDATE/DELETE all use same endpoint)
    addReward: build.mutation<RewardResponse, RewardRequest>({
      query: (data) => {
        const formData = new URLSearchParams();
        formData.append('reward_name', data.reward_name.trim());
        formData.append('amount', data.amount.trim());
        formData.append('points', data.points.trim());
        
        // Only add is_default if it's   (to set as default)
        if (data.is_default === true) {
          formData.append('is_default', '1');
        }

        return {
          url: 'user/rewardconversion',
          method: 'POST',
          body: formData,
        };
      },
      invalidatesTags: ['Reward'],
    }),

    // Set default reward (using the same endpoint with id)
    setDefaultReward: build.mutation<RewardResponse, SetDefaultRequest>({
      query: (data) => {
        const formData = new URLSearchParams();
        formData.append('id', data.id.toString());
        formData.append('is_default', '1');

        return {
          url: 'user/rewardconversion',
          method: 'POST',
          body: formData,
        };
      },
      invalidatesTags: ['Reward'],
    }),
  }),
});

export const {
  useListRewardsQuery,
  useAddRewardMutation,
  useSetDefaultRewardMutation,
} = rewardApi;